import { useCallback, useEffect, useState } from "react";
import { FilePdf, Briefcase, CurrencyCircleDollar, Lock, FloppyDisk, PaperPlaneRight } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import LicenseUploader from "@/components/LicenseUploader";
import { fmtDT } from "@/components/SubmissionsTable";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";

const REQUIRED_FIELDS = [
  ["full_name", "Full Legal Name", "text"],
  ["phone", "Phone Number", "text"],
  ["emergency_contact", "Emergency Contact", "text"],
  ["bank_name", "Bank Name", "text"],
  ["bank_account", "Bank Account Number", "text"],
  ["date_of_hire", "Date of Hire", "date"],
];

const EMPLOYEE_ROLES = ["driver", "runner"];

export function ProfileEditForm({ profile, forced = false, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(REQUIRED_FIELDS.map(([k]) => [k, profile?.[k] || ""])));
  const [busy, setBusy] = useState(false);
  const needsApproval = !forced && EMPLOYEE_ROLES.includes(profile?.role);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.patch("/profile/me", form);
      if (r.data?.status === "pending_approval") {
        toast.success("Profile update sent to admin for approval");
      } else {
        toast.success("Profile saved");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid="profile-edit-form">
      {forced && (
        <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-4">
          <p className="font-display text-lg font-bold text-amber-400">Complete your profile to continue</p>
          <p className="mt-1 text-sm text-muted-foreground">All fields are mandatory for new staff registration. You cannot access your dashboard until they are filled and saved.</p>
        </div>
      )}
      {needsApproval && (
        <div className="rounded-sm border border-primary/40 bg-primary/5 p-4">
          <p className="text-sm text-muted-foreground">
            As an employee, your edits are sent to an <span className="font-bold text-foreground">admin for approval</span> before they take effect.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {REQUIRED_FIELDS.map(([key, label, type]) => (
          <div key={key} className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label} *</Label>
            <Input data-testid={`profile-edit-${key}`} type={type} required value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
        ))}
      </div>
      <Button data-testid="profile-save-button" type="submit" disabled={busy}
        className="rounded-sm bg-primary px-8 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
        {needsApproval ? <PaperPlaneRight size={16} className="mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
        {busy ? "Sending..." : needsApproval ? "Request Profile Update" : "Save Profile"}
      </Button>
    </form>
  );
}

function CommissionSummary() {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/profile/commissions?period=${period}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [period]);

  const jobs = data?.jobs || [];

  return (
    <div className="overflow-hidden rounded-sm border border-border" data-testid="commission-summary">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-5 py-3">
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">My Commissions</span>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger data-testid="commission-period-select" className="w-36 rounded-sm border-input bg-background focus:ring-primary">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="week" data-testid="commission-period-week">This Week</SelectItem>
            <SelectItem value="month" data-testid="commission-period-month">This Month</SelectItem>
            <SelectItem value="year" data-testid="commission-period-year">This Year</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="border-b border-border bg-card px-5 py-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Total earned · {period}</p>
        <p className="mt-1 font-display text-3xl font-black text-primary" data-testid="commission-total">
          RM {(data?.total ?? 0).toFixed(2)}
        </p>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Job No</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Service</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Route</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Role</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Commission</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((j, i) => (
              <TableRow key={`${j.job_no}-${i}`} className="border-border hover:bg-muted/50" data-testid={`commission-row-${j.job_no}`}>
                <TableCell className="font-mono text-sm font-bold text-primary">{j.job_no}</TableCell>
                <TableCell className="max-w-[180px] truncate text-sm">{j.service_type || "—"}</TableCell>
                <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">{j.route}</TableCell>
                <TableCell className="text-xs font-bold uppercase tracking-wider">{j.role}</TableCell>
                <TableCell className="text-right font-mono text-sm font-bold text-primary">RM {j.commission.toFixed(2)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDT(j.submitted_at)}</TableCell>
              </TableRow>
            ))}
            {!jobs.length && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No commissioned jobs in this period.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/profile/password", form);
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-sm border border-border bg-card p-6" data-testid="password-form">
      <h3 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
        <Lock size={20} className="text-primary" /> Change Password
      </h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Current Password</Label>
          <Input data-testid="pw-current" type="password" required value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            className="rounded-sm border-input bg-background focus-visible:ring-primary" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">New Password</Label>
          <Input data-testid="pw-new" type="password" required minLength={6} value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className="rounded-sm border-input bg-background focus-visible:ring-primary" />
        </div>
      </div>
      <Button data-testid="pw-save-button" type="submit" disabled={busy} variant="outline"
        className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
        {busy ? "Updating..." : "Update Password"}
      </Button>
    </form>
  );
}

export default function ProfilePanel({ user, forced = false, defaultTab = "profile", payslipsOnly = false }) {
  const [profile, setProfile] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [editing, setEditing] = useState(forced);

  const refresh = useCallback(() => {
    api.get("/profile/me").then((r) => {
      setProfile(r.data);
      if (!r.data.profile_complete) setEditing(true);
    }).catch((e) => toast.error(formatApiError(e)));
    api.get("/payroll/my").then((r) => setPayslips(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!profile) return null;

  if (forced) {
    return <ProfileEditForm profile={profile} forced onSaved={() => window.location.reload()} />;
  }

  const req = profile.profile_update_request;

  const fields = [
    ["Full Legal Name", profile.full_name || "—"],
    ["Email", profile.email],
    ["Phone", profile.phone || "—"],
    ["Emergency Contact", profile.emergency_contact || "—"],
    ["Bank", profile.bank_name ? `${profile.bank_name} · ${profile.bank_account || "—"}` : "—"],
    ["Date of Hire", profile.date_of_hire || "—"],
    ["Base Salary", profile.base_salary ? `RM ${Number(profile.base_salary).toFixed(2)} / month` : "—"],
  ];

  return (
    <Tabs defaultValue={payslipsOnly ? "payslips" : defaultTab} data-testid="profile-panel">
      {!payslipsOnly && (
        <TabsList className="mb-6 rounded-sm border border-border bg-card">
          <TabsTrigger data-testid="tab-my-profile" value="profile" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">My Profile</TabsTrigger>
          <TabsTrigger data-testid="tab-my-payslips" value="payslips" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">My Payslips</TabsTrigger>
        </TabsList>
      )}

      <TabsContent value="profile" className="space-y-6">
        <div className="grid grid-cols-2 gap-px border border-border/60 bg-border/40 lg:grid-cols-4">
          <div className="bg-card p-6" data-testid="profile-stat-jobs">
            <Briefcase size={22} className="text-primary" />
            <p className="mt-3 font-display text-3xl font-black">{profile.stats?.jobs ?? 0}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Total Jobs</p>
          </div>
          <div className="bg-card p-6" data-testid="profile-stat-commissions">
            <CurrencyCircleDollar size={22} className="text-primary" />
            <p className="mt-3 font-display text-3xl font-black text-primary">RM {(profile.stats?.commissions ?? 0).toFixed(2)}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Commissions Earned (All Time)</p>
          </div>
          <div className="bg-card p-6 lg:col-span-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Role</p>
            <Badge variant="outline" className="mt-2 rounded-sm font-display text-sm font-bold uppercase tracking-widest text-primary" data-testid="profile-role-badge">{profile.role}</Badge>
            <p className="mt-2 text-xs text-muted-foreground">
              {profile.role === "runner" ? "Upload a GDL license and get it verified to qualify for the Driver role." : "Account in good standing."}
            </p>
          </div>
        </div>

        {EMPLOYEE_ROLES.includes(profile.role) && <CommissionSummary />}

        {req?.status === "pending" && (
          <div className="flex flex-wrap items-center gap-3 rounded-sm border border-amber-500/40 bg-amber-500/5 px-4 py-3" data-testid="profile-request-pending-banner">
            <Badge variant="outline" className="rounded-sm border-amber-500/50 bg-amber-500/10 font-bold uppercase tracking-wider text-amber-400">Awaiting Approval</Badge>
            <span className="text-xs text-muted-foreground">Your profile update was sent to the admin and is pending review.</span>
          </div>
        )}
        {req?.status === "rejected" && (
          <div className="flex flex-wrap items-center gap-3 rounded-sm border border-red-500/40 bg-red-500/5 px-4 py-3" data-testid="profile-request-rejected-banner">
            <Badge variant="outline" className="rounded-sm border-red-500/50 bg-red-500/10 font-bold uppercase tracking-wider text-red-400">Rejected</Badge>
            <span className="text-xs text-muted-foreground">Your last profile update request was rejected. You may submit a new one.</span>
          </div>
        )}

        {editing ? (
          <ProfileEditForm profile={profile} onSaved={() => { setEditing(false); refresh(); }} />
        ) : (
          <div className="rounded-sm border border-border" data-testid="profile-details">
            <div className="flex items-center justify-between border-b border-border bg-card px-5 py-3">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Personal Details</span>
              <Button size="sm" variant="outline" data-testid="profile-edit-button" onClick={() => setEditing(true)}
                className="rounded-sm border-input hover:bg-secondary hover:text-foreground">Edit</Button>
            </div>
            <div className="grid grid-cols-1 gap-px bg-border/40 md:grid-cols-2">
              {fields.map(([k, v]) => (
                <div key={k} className="bg-card p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{k}</p>
                  <p className="mt-1 text-sm font-semibold">{v}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-sm border border-border p-6" data-testid="profile-license-section">
          <p className="mb-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">Driving License</p>
          <LicenseUploader uid={user.id} license={profile.license} onDone={refresh} />
        </div>

        <PasswordCard />
      </TabsContent>

      <TabsContent value="payslips">
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="payslips-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Week</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Base</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Commissions</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Total</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.map((p) => (
                <TableRow key={p.id} className="border-border hover:bg-muted/50" data-testid={`my-payslip-${p.id}`}>
                  <TableCell className="font-mono text-sm">{p.week_start} → {p.week_end}</TableCell>
                  <TableCell className="text-right font-mono">RM {p.base_weekly.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-mono">RM {p.commission_total.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-primary">RM {p.total.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`rounded-sm font-bold uppercase ${p.status === "paid" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}
                      data-testid={`my-payslip-status-${p.id}`}>{p.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {p.status === "paid" ? (
                      <Button size="sm" variant="outline" data-testid={`my-payslip-download-${p.id}`}
                        onClick={() => downloadFile(`/payroll/payslips/${p.id}/download`, `payslip-${p.week_start}.pdf`).catch((e) => toast.error(formatApiError(e)))}
                        className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                        <FilePdf size={14} className="mr-1" /> Download
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground">Available once paid</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!payslips.length && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No payslips yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
    </Tabs>
  );
}
